if (!customElements.get('product-center')) {
  class ProductCenter extends HTMLElement {
    connectedCallback() {
      this.storeDomain = this.dataset.storeDomain || '';
      this.storefrontToken = this.dataset.storefrontToken || '';
      this.fallbackCollectionHandle = this.cleanValue(this.dataset.collectionHandle);
      this.config = this.readConfig();
      this.products = [];
      this.filteredProducts = [];
      this.selectedFilters = new Map();
      this.urlState = this.readUrlState();
      this.selectedCompareProducts = this.readCompareItems();
      this.activeCategory = '';
      this.searchTerm = this.cleanValue(this.config.defaultSearch);
      this.isLoadingProducts = false;
      this.requestId = 0;
      this.fullCatalogLoaded = false;
      this.fullCatalog = [];
      this.searchDebounceTimer = null;
      this.handleProductCenterMenuClick = (event) => this.onProductCenterMenuClick(event);
      this.handlePopState = () => this.applyProductCenterUrl(new URL(window.location.href), { updateHistory: false, shouldScroll: false });
      this.handleResize = () => {
        this.syncFilterPanelHeight();
        this.updateGroupsNavOverflow();
      };
      this.handleGroupsNavScroll = () => this.updateGroupsNavOverflow();
      this.handleGroupsNavWheel = (event) => this.scrollGroupsNavByWheel(event);
      this.handleFilterScroll = () => this.updateFilterScrollbar();
      this.handleFilterScrollbarPointerMove = (event) => this.onFilterScrollbarPointerMove(event);
      this.handleFilterScrollbarPointerUp = () => this.onFilterScrollbarPointerUp();
      this.filterPanelResizeObserver = null;
      this.filterScrollbarDrag = null;
      this.groupsSpy = null;

      this.nodes = {
        filterPanel: this.querySelector('.product-center__filters'),
        filters: this.querySelector('[data-product-center-filters]'),
        tabs: this.querySelector('[data-product-center-tabs]'),
        grid: this.querySelector('[data-product-center-grid]'),
        groupsNav: this.querySelector('[data-product-center-groups-nav]'),
        summary: this.querySelector('[data-product-center-summary]'),
        status: this.querySelector('[data-product-center-status]'),
        compareBar: this.querySelector('[data-product-center-compare-bar]'),
        compareCount: this.querySelector('[data-product-center-compare-count]'),
        reset: this.querySelector('[data-product-center-reset]'),
        filterVisibility: this.querySelector('[data-product-center-filter-visibility]'),
        searchForm: this.querySelector('[data-product-center-search-form]'),
        search: this.querySelector('[data-product-center-search]')
      };

      this.icons = {};
      this.querySelectorAll('[data-product-center-icon]').forEach((node) => {
        this.icons[node.dataset.productCenterIcon] = node.innerHTML;
      });

      this.manualFilterGroups = this.parseFilterGroups(this.config.filterGroups);
      this.metaobjectFilterGroupConfigs = this.parseMetaobjectFilterGroups(this.config.metaobjectFilterGroups);
      this.cardSpecs = this.parseCardSpecs(this.config.cardSpecs);
      this.cardRibbonField = this.normalizeMetafieldRef(this.config.cardRibbonField || 'custom.subcategory').canonical;
      this.cardTitleField = this.normalizeMetafieldRef(this.config.cardTitleField || 'custom.original_name').canonical;
      this.groupField = this.normalizeMetafieldRef(this.config.groupField || 'custom.subcategory').canonical;
      this.datasheetField = this.normalizeMetafieldRef(this.config.datasheetField || '').canonical;
      this.tableColumns = this.parseTableColumns(this.config.tableColumns);
      this.applyUrlFilters(this.urlState.filters);
      this.metafieldRefs = this.collectMetafieldRefs();
      this.filterGroups = this.manualFilterGroups;
      this.categories = Array.isArray(this.config.categories) ? this.config.categories.filter((item) => item?.label) : [];
      this.activeCategory = this.categories[0]?.value || this.categories[0]?.collectionHandle || this.categories[0]?.label || '';
      this.applyUrlState();
      if (this.nodes.search) this.nodes.search.value = this.searchTerm;

      this.renderFilters();
      this.renderTabs();
      this.renderCompareBar();
      this.ensureFilterScrollbar();
      this.bindEvents();
      this.observeFilterPanelHeight();
      this.loadMetaobjectFilters();
      this.loadProducts();
      this.scrollToProductCenterFromUrlState();
    }

    readConfig() {
      const script = this.querySelector('[data-product-center-config]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    readUrlState(rawSearch = window.location.search) {
      const state = {
        category: '',
        search: '',
        group: '',
        filters: []
      };

      try {
        this.urlSearchCandidates(rawSearch).forEach((search) => {
          this.mergeUrlSearchState(search, state);
        });
      } catch (_error) {
        return state;
      }

      return state;
    }

    urlSearchCandidates(rawSearch) {
      const candidates = [];
      let current = this.cleanValue(rawSearch);

      for (let index = 0; index < 3 && current; index += 1) {
        const normalized = current.replace(/^\?&/, '?');
        if (!candidates.includes(normalized)) candidates.push(normalized);

        try {
          const decoded = decodeURIComponent(current);
          if (decoded === current) break;
          current = decoded;
        } catch (_error) {
          break;
        }
      }

      return candidates;
    }

    mergeUrlSearchState(search, state) {
      const params = new URLSearchParams(search);
      const category = this.cleanValue(params.get('pc_category') || params.get('pc_collection'));
      const searchTerm = this.cleanValue(params.get('pc_search') || params.get('pc_q'));
      const groupAnchor = this.cleanValue(params.get('pc_group'));

      if (category && !state.category) state.category = category;
      if (searchTerm && !state.search) state.search = searchTerm;
      if (groupAnchor && !state.group) state.group = groupAnchor;

      params.getAll('pc_filter').forEach((value) => {
        this.parseUrlFilterValue(value).forEach((filter) => state.filters.push(filter));
      });

      params.forEach((value, key) => {
        const match = key.match(/^pc_filter\[(.+)]$/);
        if (!match) return;
        const field = this.cleanValue(match[1]);
        const option = this.cleanValue(value);
        if (field && option) state.filters.push({ key: field, value: option });
      });
    }

    parseUrlFilterValue(rawValue) {
      return String(rawValue || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => {
          const separatorIndex = this.firstSeparatorIndex(item, [':', '=', '|']);
          if (separatorIndex < 1) return null;
          const key = this.cleanValue(item.slice(0, separatorIndex));
          const value = this.cleanValue(item.slice(separatorIndex + 1));
          return key && value ? { key, value } : null;
        })
        .filter(Boolean);
    }

    firstSeparatorIndex(value, separators) {
      const indexes = separators
        .map((separator) => value.indexOf(separator))
        .filter((index) => index > -1)
        .sort((a, b) => a - b);
      return indexes.length ? indexes[0] : -1;
    }

    applyUrlFilters(filters) {
      if (!Array.isArray(filters)) return;
      filters.forEach((filter) => {
        const ref = this.normalizeMetafieldRef(filter?.key);
        const value = this.cleanValue(filter?.value);
        if (!ref.canonical || !value) return;
        if (!this.selectedFilters.has(ref.canonical)) this.selectedFilters.set(ref.canonical, new Set());
        this.selectedFilters.get(ref.canonical).add(value);
      });
    }

    applyUrlState({ reset = false } = {}) {
      if (reset) {
        this.searchTerm = '';
        this.activeCategory = this.categories[0]?.value || this.categories[0]?.collectionHandle || this.categories[0]?.label || '';
      }

      const search = this.cleanValue(this.urlState?.search);
      if (search) this.searchTerm = search;

      const category = this.matchCategoryValue(this.urlState?.category);
      if (category) this.activeCategory = category;
    }

    matchCategoryValue(rawValue) {
      const target = this.cleanValue(rawValue);
      if (!target) return '';

      const targetToken = this.normalizeUrlToken(target);
      const matched = this.categories.find((category) => {
        const value = category.value || category.collectionHandle || category.label;
        const candidates = [
          value,
          category.collectionHandle,
          category.label,
          category.url,
          this.categoryHandleFromUrl(category.url)
        ].map((item) => this.cleanValue(item)).filter(Boolean);

        return candidates.some((candidate) => (
          candidate === target ||
          this.normalizeUrlToken(candidate) === targetToken
        ));
      });

      return matched ? matched.value || matched.collectionHandle || matched.label || '' : '';
    }

    categoryHandleFromUrl(url) {
      const value = this.cleanValue(url);
      if (!value) return '';
      const match = value.match(/\/collections\/([^/?#]+)/);
      return match ? match[1] : '';
    }

    bindEvents() {
      this.nodes.reset?.addEventListener('click', () => {
        this.selectedFilters.clear();
        this.searchTerm = '';
        this.clearSearchDebounce();
        if (this.nodes.search) this.nodes.search.value = '';
        this.renderFilters();
        this.renderTabs();
        this.syncProductCenterUrlState({ replace: true });
        this.loadProducts();
      });

      this.nodes.filterVisibility?.addEventListener('click', () => {
        const collapsed = !this.classList.contains('is-filter-groups-collapsed');
        this.classList.toggle('is-filter-groups-collapsed', collapsed);
        this.querySelectorAll('.product-center__filter-group').forEach((group) => {
          group.classList.toggle('is-collapsed', collapsed);
        });
        this.nodes.filterVisibility.textContent = collapsed ? 'Show Filter' : 'Hide Filter';
        this.nodes.filterVisibility.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        this.syncFilterPanelHeight();
      });

      this.nodes.searchForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.searchTerm = this.cleanValue(this.nodes.search?.value);
        this.clearSearchDebounce();
        this.renderTabs();
        this.syncProductCenterUrlState({ replace: true });
        this.loadProducts();
      });

      this.nodes.search?.addEventListener('input', () => {
        this.searchTerm = this.cleanValue(this.nodes.search.value);
        this.renderTabs();
        this.syncProductCenterUrlState({ replace: true });
        this.scheduleProductLoad();
      });

      document.addEventListener('click', this.handleProductCenterMenuClick);
      window.addEventListener('popstate', this.handlePopState);
      window.addEventListener('resize', this.handleResize);
      this.nodes.filters?.addEventListener('scroll', this.handleFilterScroll, { passive: true });
      this.nodes.groupsNav?.addEventListener('scroll', this.handleGroupsNavScroll, { passive: true });
      this.nodes.groupsNav?.addEventListener('wheel', this.handleGroupsNavWheel, { passive: false });

      this.addEventListener('change', (event) => {
        const compareInput = event.target.closest('[data-product-center-compare-input]');
        if (compareInput) {
          this.toggleCompareProduct(compareInput);
          return;
        }

        const input = event.target.closest('[data-product-center-filter]');
        if (!input) return;
        const key = input.dataset.key;
        const value = input.value;
        if (!this.selectedFilters.has(key)) this.selectedFilters.set(key, new Set());
        const values = this.selectedFilters.get(key);
        if (input.checked) {
          values.add(value);
        } else {
          values.delete(value);
          if (values.size === 0) this.selectedFilters.delete(key);
        }
        if (this.isChipPlatformFilter(key)) {
          this.reconcileSystemOnChipSelection();
          this.renderFilters();
        }
        this.syncProductCenterUrlState({ replace: true });
        this.applyFilters();
      });

      this.addEventListener('click', (event) => {
        const searchClear = event.target.closest('[data-product-center-search-clear]');
        if (searchClear) {
          // 与点击分类 tab 的清除逻辑同序列：清搜索词/输入框 → 同步 URL → 重新加载
          this.searchTerm = '';
          this.clearSearchDebounce();
          if (this.nodes.search) this.nodes.search.value = '';
          this.renderTabs();
          this.syncProductCenterUrlState();
          this.loadProducts();
          return;
        }

        const groupsNavLink = event.target.closest('[data-product-center-group-nav]');
        if (groupsNavLink) {
          const section = this.querySelector(`#${CSS.escape(groupsNavLink.dataset.productCenterGroupNav)}`);
          this.scrollToGroupSection(section);
          return;
        }

        const groupToggle = event.target.closest('[data-product-center-group-toggle]');
        if (groupToggle) {
          const section = groupToggle.closest('.product-center__grouptable');
          const collapsed = !section?.classList.contains('is-collapsed');
          section?.classList.toggle('is-collapsed', collapsed);
          groupToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
          this.syncFilterPanelHeight();
          return;
        }

        const toggle = event.target.closest('[data-product-center-filter-toggle]');
        if (toggle) {
          toggle.closest('.product-center__filter-group')?.classList.toggle('is-collapsed');
          this.syncFilterVisibilityButton();
          this.syncFilterPanelHeight();
          return;
        }

        const more = event.target.closest('[data-product-center-more]');
        if (more) {
          const group = more.closest('.product-center__filter-group');
          const expanded = group?.classList.toggle('is-expanded');
          more.textContent = expanded ? 'Less' : 'More';
          this.syncFilterPanelHeight();
          return;
        }

        const tab = event.target.closest('[data-product-center-category]');
        if (tab) {
          // 一页渲染:tab 是锚点,点击滚到对应大分类区块(搜索结果同样分区);高亮由滚动 spy 接管
          const token = this.normalize(tab.dataset.value || '').replace(/\s+/g, '-');
          const section = this.querySelector(`#pc-cat-${CSS.escape(token)}`);
          this.scrollToGroupSection(section);
          return;
        }

        const removeCompare = event.target.closest('[data-product-center-compare-remove]');
        if (removeCompare) {
          this.removeCompareProduct(removeCompare.dataset.productCenterCompareRemove);
          return;
        }

        const clearCompare = event.target.closest('[data-product-center-compare-clear]');
        if (clearCompare) {
          this.clearCompareProducts();
          return;
        }

        const compareAction = event.target.closest('[data-product-center-compare-action]');
        if (compareAction) {
          this.goCompare();
        }
      });
    }

    disconnectedCallback() {
      this.clearSearchDebounce();
      document.removeEventListener('click', this.handleProductCenterMenuClick);
      window.removeEventListener('popstate', this.handlePopState);
      window.removeEventListener('resize', this.handleResize);
      this.nodes.filters?.removeEventListener('scroll', this.handleFilterScroll);
      this.nodes.groupsNav?.removeEventListener('scroll', this.handleGroupsNavScroll);
      this.nodes.groupsNav?.removeEventListener('wheel', this.handleGroupsNavWheel);
      window.removeEventListener('pointermove', this.handleFilterScrollbarPointerMove);
      window.removeEventListener('pointerup', this.handleFilterScrollbarPointerUp);
      this.filterPanelResizeObserver?.disconnect();
    }

    clearSearchDebounce() {
      if (!this.searchDebounceTimer) return;
      window.clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    scheduleProductLoad() {
      this.clearSearchDebounce();
      this.searchDebounceTimer = window.setTimeout(() => {
        this.searchDebounceTimer = null;
        this.loadProducts();
      }, this.hasSearchTerm() ? 260 : 0);
    }

    onProductCenterMenuClick(event) {
      const link = event.target.closest('.products-mega-menu a[href]');
      if (!link) return;
      if (link.closest('footer, .section-footer, .firefly-footer-desktop, .firefly-footer-mobile')) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      let url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (_error) {
        return;
      }

      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      if (!this.hasProductCenterParams(url)) return;

      event.preventDefault();
      this.applyProductCenterUrl(url);
    }

    applyProductCenterUrl(url, { updateHistory = true, shouldScroll = true } = {}) {
      const currentUrl = new URL(window.location.href);
      if (updateHistory && (url.search !== currentUrl.search || url.hash !== currentUrl.hash)) {
        window.history.pushState({ path: url.href }, '', url.href);
      }

      const prevCategory = this.activeCategory;
      const prevSearchTerm = this.searchTerm;
      this.urlState = this.readUrlState(url.search);
      this.selectedFilters.clear();
      this.applyUrlFilters(this.urlState.filters);
      this.applyUrlState({ reset: true });
      // 菜单软切换 / 前进后退到达本页时重新允许 pc_group 定位：
      // 首次进页已消费过一次，不重置的话页内再点菜单分组不会滚动
      this.groupAnchorConsumed = false;
      if (this.nodes.search) this.nodes.search.value = this.searchTerm;

      this.clearSearchDebounce();
      this.renderTabs();
      this.renderFilters();

      // 分类 tab 与搜索词都没变（如同一大类下点不同子分类分组）时，
      // 产品数据范围不变，无需重新拉取：重新过滤渲染后直接重新定位分组。
      // 定位成功时不能再 scrollToProductCenter——两个 smooth 滚动同时发起，
      // 后者会把分组定位覆盖掉（loadProducts 是异步的，先滚顶部、
      // 接口返回后再定位分组，所以换大类路径没有这个冲突）
      if (
        this.activeCategory === prevCategory &&
        this.searchTerm === prevSearchTerm &&
        this.products.length
      ) {
        this.applyFilters();
        const anchored = this.scrollToGroupFromUrlState();
        if (!anchored && shouldScroll) this.scrollToProductCenter();
      } else {
        this.loadProducts();
        if (shouldScroll) {
          // 全店目录已在内存时 loadProducts 同步完成,目标分组/大类此刻已渲染,
          // 立即定位且成功后不得再滚顶部(两个 smooth 滚动并发,后发的会覆盖先发的);
          // 走网络加载时区块尚未渲染、定位返回 false,先滚顶部,
          // 接口返回后 loadProducts 内部再定位(与首次进页同时序)
          const anchored = this.scrollToGroupFromUrlState();
          if (!anchored) this.scrollToProductCenter();
        }
      }
    }

    isProductCenterUrl(url) {
      return (
        this.hasProductCenterParams(url) ||
        url.pathname.includes('/pages/product-center')
      );
    }

    hasProductCenterParams(url) {
      return (
        url.searchParams.has('pc_category') ||
        url.searchParams.has('pc_collection') ||
        url.searchParams.has('pc_page') ||
        url.searchParams.has('pc_filter') ||
        url.searchParams.has('pc_search') ||
        url.searchParams.has('pc_q')
      );
    }

    scrollToProductCenterFromUrlState() {
      if (!this.hasUrlState()) return;
      window.requestAnimationFrame(() => {
        window.setTimeout(() => this.scrollToProductCenter(), 60);
      });
    }

    hasUrlState() {
      return Boolean(
        this.cleanValue(this.urlState?.category) ||
        this.cleanValue(this.urlState?.search) ||
        this.cleanValue(this.urlState?.group) ||
        this.urlState?.filters?.length
      );
    }

    syncProductCenterUrlState({ replace = false } = {}) {
      let url;
      try {
        url = new URL(window.location.href);
      } catch (_error) {
        return;
      }

      const category = this.cleanValue(this.activeCategory);
      if (category) {
        url.searchParams.set('pc_category', category);
      } else {
        url.searchParams.delete('pc_category');
      }
      url.searchParams.delete('pc_collection');

      url.searchParams.delete('pc_filter');
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (/^pc_filter\[.+\]$/.test(key)) url.searchParams.delete(key);
      });
      this.selectedFilters.forEach((values, key) => {
        const ref = this.normalizeMetafieldRef(key);
        const filterKey = ref.namespace === 'custom' ? ref.shortKey : ref.canonical;
        if (!filterKey) return;
        values.forEach((value) => {
          const filterValue = this.cleanValue(value);
          if (filterValue) url.searchParams.append('pc_filter', `${filterKey}:${filterValue}`);
        });
      });

      url.searchParams.delete('pc_q');
      const searchTerm = this.cleanValue(this.searchTerm);
      if (searchTerm) {
        url.searchParams.set('pc_search', searchTerm);
      } else {
        url.searchParams.delete('pc_search');
      }

      url.searchParams.delete('pc_page');

      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const next = `${url.pathname}${url.search}${url.hash}`;
      if (next === current) return;

      const method = replace ? 'replaceState' : 'pushState';
      window.history[method]({ path: url.href }, '', url.href);
      this.urlState = this.readUrlState(url.search);
    }

    scrollToProductCenter() {
      const offset = window.innerWidth < 768 ? 76 : 96;
      const pageWrapper = document.querySelector('.page-wrapper');
      const pageWrapperStyle = pageWrapper ? window.getComputedStyle(pageWrapper) : null;
      const pageWrapperScrollable = pageWrapper &&
        (pageWrapperStyle.overflowY === 'auto' || pageWrapperStyle.overflowY === 'scroll') &&
        pageWrapper.scrollHeight > pageWrapper.clientHeight;

      if (pageWrapperScrollable) {
        const wrapperRect = pageWrapper.getBoundingClientRect();
        const top = Math.max(0, pageWrapper.scrollTop + this.getBoundingClientRect().top - wrapperRect.top - offset);
        pageWrapper.scrollTo({ top, behavior: 'auto' });
        return;
      }

      const top = Math.max(0, this.getBoundingClientRect().top + window.scrollY - offset);
      window.scrollTo({ top, behavior: 'auto' });
    }

    syncFilterVisibilityButton() {
      if (!this.nodes.filterVisibility) return;
      const groups = Array.from(this.querySelectorAll('.product-center__filter-group'));
      const collapsed = groups.length > 0 && groups.every((group) => group.classList.contains('is-collapsed'));
      this.classList.toggle('is-filter-groups-collapsed', collapsed);
      this.nodes.filterVisibility.textContent = collapsed ? 'Show Filter' : 'Hide Filter';
      this.nodes.filterVisibility.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }

    readCompareItems() {
      try {
        const parsed = JSON.parse(window.localStorage.getItem('pcmp_selected_products_v1') || '[]');
        if (!Array.isArray(parsed)) return [];
        const seen = new Set();
        return parsed
          .map((item) => ({
            handle: this.cleanValue(item?.handle).toLowerCase(),
            title: this.cleanValue(item?.title),
            image: this.cleanValue(item?.image)
          }))
          .filter((item) => {
            if (!item.handle || seen.has(item.handle)) return false;
            seen.add(item.handle);
            return true;
          })
          .slice(0, 5);
      } catch (_error) {
        return [];
      }
    }

    writeCompareItems() {
      try {
        window.localStorage.setItem('pcmp_selected_products_v1', JSON.stringify(this.selectedCompareProducts));
      } catch (_error) {
        /* no-op */
      }
    }

    isCompareSelected(handle) {
      const normalized = this.cleanValue(handle).toLowerCase();
      return this.selectedCompareProducts.some((item) => item.handle === normalized);
    }

    // Accessories 大分类下配件无统一对比参数：Compare 置灰不可勾（渲染时同步禁用 input）。
    // 一页渲染下按「产品所在区块的大分类」判定；分类 value 是 collection handle
    // （如 development-accessories），故用包含匹配
    isCompareDisabledByCategory(category) {
      const value = this.cleanValue(category?.value || category?.collectionHandle || category?.label || this.activeCategory);
      return this.normalize(value).includes('accessor');
    }

    toggleCompareProduct(input) {
      const item = {
        handle: this.cleanValue(input.dataset.productHandle).toLowerCase(),
        title: this.cleanValue(input.dataset.productTitle),
        image: this.cleanValue(input.dataset.productImage)
      };

      if (!item.handle) return;

      if (input.checked) {
        if (!this.isCompareSelected(item.handle)) {
          if (this.selectedCompareProducts.length >= 5) {
            input.checked = false;
            window.alert('You can compare up to 5 products.');
            return;
          }
          this.selectedCompareProducts.push(item);
        }
      } else {
        this.selectedCompareProducts = this.selectedCompareProducts.filter((product) => product.handle !== item.handle);
      }

      this.writeCompareItems();
      this.syncCompareInputs();
      this.renderCompareBar();
    }

    removeCompareProduct(handle) {
      const normalized = this.cleanValue(handle).toLowerCase();
      this.selectedCompareProducts = this.selectedCompareProducts.filter((product) => product.handle !== normalized);
      this.writeCompareItems();
      this.syncCompareInputs();
      this.renderCompareBar();
    }

    clearCompareProducts() {
      this.selectedCompareProducts = [];
      this.writeCompareItems();
      this.syncCompareInputs();
      this.renderCompareBar();
    }

    syncCompareInputs() {
      this.querySelectorAll('[data-product-center-compare-input]').forEach((input) => {
        input.checked = this.isCompareSelected(input.dataset.productHandle);
        input.closest('.product-center__row-action--compare')?.classList.toggle('is-checked', input.checked);
      });
      this.updateCompareCount();
    }

    updateCompareCount() {
      if (this.nodes.compareCount) {
        this.nodes.compareCount.textContent = String(this.selectedCompareProducts.length);
      }
    }

    renderCompareBar() {
      this.updateCompareCount();
      if (!this.nodes.compareBar) return;
      const items = this.selectedCompareProducts;
      this.nodes.compareBar.hidden = items.length === 0;
      this.classList.toggle('has-compare-bar', items.length > 0);
      if (items.length === 0) {
        this.nodes.compareBar.innerHTML = '';
        return;
      }

      const cards = items.map((item, index) => `
        <article class="product-center__compare-item${index === items.length - 1 && items.length === 5 ? ' is-last' : ''}">
          <div class="product-center__compare-thumb">
            ${item.image ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" loading="lazy">` : ''}
          </div>
          <div class="product-center__compare-meta">
            <p title="${this.escapeHtml(item.title || item.handle)}">${this.escapeHtml(item.title || item.handle)}</p>
            <button type="button" data-product-center-compare-remove="${this.escapeHtml(item.handle)}">Remove</button>
          </div>
        </article>
      `).join('');

      this.nodes.compareBar.innerHTML = `
        <div class="product-center__compare-panel">
          <div class="product-center__compare-head">
            <span>Product</span>
            <div class="product-center__compare-actions">
              <button class="product-center__compare-clear" type="button" data-product-center-compare-clear>Clear</button>
              <button class="product-center__compare-submit" type="button" data-product-center-compare-action ${items.length < 2 ? 'disabled' : ''}>Compare</button>
            </div>
          </div>
          <div class="product-center__compare-list">
            ${cards}
            ${items.length < 5 ? '<p class="product-center__compare-add">You can continue to add</p>' : ''}
          </div>
        </div>
      `;
    }

    goCompare() {
      if (this.selectedCompareProducts.length < 2) {
        window.alert('Select at least 2 products to compare.');
        return;
      }
      const url = new URL('/pages/product-comparison', window.location.origin);
      url.searchParams.set('view', 'product-comparison');
      url.searchParams.set('ids', this.selectedCompareProducts.map((item) => item.handle).join(','));
      window.open(`${url.pathname}${url.search}`, '_blank', 'noopener,noreferrer');
    }

    renderFilters() {
      if (!this.nodes.filters) return;
      this.nodes.filters.innerHTML = this.filterGroups.map((group) => {
        const visibleOptions = this.visibleFilterOptions(group);
        const hasSelectedExtra = visibleOptions.some((option, index) => index > 4 && this.isFilterSelected(group.key, option.value));
        const options = visibleOptions.map((option, index) => `
          <label class="product-center__checkbox${index > 4 ? ' is-extra' : ''}">
            <input type="checkbox" value="${this.escapeHtml(option.value)}" data-key="${this.escapeHtml(group.key)}" data-product-center-filter>
            <span>${this.escapeHtml(option.label)}</span>
          </label>
        `).join('');

        return `
          <section class="product-center__filter-group${hasSelectedExtra ? ' is-expanded' : ''}">
            <button class="product-center__filter-toggle" type="button" data-product-center-filter-toggle>
              <span>${this.escapeHtml(group.label)}</span>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div class="product-center__filter-options">
              ${options}
              ${visibleOptions.length > 5 ? `<button class="product-center__more" type="button" data-product-center-more>${hasSelectedExtra ? 'Less' : 'More'}</button>` : ''}
            </div>
          </section>
        `;
      }).join('');
      this.syncFilterInputs();
      this.syncFilterPanelHeight();
    }

    visibleFilterOptions(group) {
      const options = Array.isArray(group?.options) ? group.options : [];
      if (!this.isSystemOnChipFilter(group?.key)) return options;

      const selectedPlatformKeys = this.selectedChipPlatformIdentityKeys();
      if (selectedPlatformKeys.size === 0) return options;

      return options.filter((option) => {
        const belongingPlatformKeys = Array.isArray(option?.belongingPlatformKeys)
          ? option.belongingPlatformKeys
          : [];
        return belongingPlatformKeys.some((key) => selectedPlatformKeys.has(key));
      });
    }

    selectedChipPlatformIdentityKeys() {
      const platformConfig = this.metaobjectFilterConfigByType('chip_platform');
      if (!platformConfig) return new Set();

      const platformGroup = this.filterGroups.find((group) => group.key === platformConfig.key);
      if (!platformGroup) return new Set();

      const selectedOptions = platformGroup.options.filter((option) => (
        this.isFilterSelected(platformGroup.key, option.value)
      ));

      return new Set(selectedOptions.flatMap((option) => option.identityKeys || []));
    }

    reconcileSystemOnChipSelection() {
      const systemOnChipConfig = this.metaobjectFilterConfigByType('system_on_chip');
      if (!systemOnChipConfig) return;

      const selectedValues = this.selectedFilters.get(systemOnChipConfig.key);
      if (!selectedValues?.size) return;

      const systemOnChipGroup = this.filterGroups.find((group) => group.key === systemOnChipConfig.key);
      if (!systemOnChipGroup) return;

      const visibleValues = new Set(
        this.visibleFilterOptions(systemOnChipGroup).map((option) => this.normalizeUrlToken(option.value))
      );

      Array.from(selectedValues).forEach((value) => {
        if (!visibleValues.has(this.normalizeUrlToken(value))) selectedValues.delete(value);
      });
      if (selectedValues.size === 0) this.selectedFilters.delete(systemOnChipConfig.key);
    }

    metaobjectFilterConfigByType(type) {
      const target = this.normalizeUrlToken(type);
      return this.metaobjectFilterGroupConfigs.find((group) => this.normalizeUrlToken(group.type) === target) || null;
    }

    isChipPlatformFilter(key) {
      return this.metaobjectFilterConfigByType('chip_platform')?.key === this.normalizeMetafieldRef(key).canonical;
    }

    isSystemOnChipFilter(key) {
      return this.metaobjectFilterConfigByType('system_on_chip')?.key === this.normalizeMetafieldRef(key).canonical;
    }

    syncFilterInputs() {
      this.querySelectorAll('[data-product-center-filter]').forEach((input) => {
        input.checked = this.isFilterSelected(input.dataset.key, input.value);
      });
    }

    isFilterSelected(key, value) {
      const ref = this.normalizeMetafieldRef(key);
      const selectedValues = this.selectedFilters.get(ref.canonical);
      if (!selectedValues?.size) return false;
      const target = this.normalizeUrlToken(value);
      return Array.from(selectedValues).some((selected) => this.normalizeUrlToken(selected) === target);
    }

    renderTabs() {
      if (!this.nodes.tabs) return;
      // 左侧筛选/搜索后没产品的大分类 tab 一并隐藏,与下方区块同步;
      // 筛到零结果时保留全部 tab(主区显示 empty 文案,tab 条空了会缺冻结链一段)
      const visibleCategories = this.filteredProducts.length > 0
        ? this.categories.filter((category) => this.categoryHasProducts(category))
        : this.categories;
      const values = visibleCategories.map((category) => category.value || category.collectionHandle || category.label);
      // 高亮优先跟随 activeCategory;它对应的 tab 被隐藏时(如 URL 恢复落在空分类)取第一个可见 tab
      const activeValue = values.includes(this.activeCategory) ? this.activeCategory : values[0] || '';
      this.nodes.tabs.innerHTML = visibleCategories.map((category) => {
        const value = category.value || category.collectionHandle || category.label;
        const active = value === activeValue;
        return `<button class="product-center__tab${active ? ' is-active' : ''}" type="button" data-product-center-category data-value="${this.escapeHtml(value)}">${this.escapeHtml(category.label)}</button>`;
      }).join('');
      this.scrollActiveTabIntoView();
    }

    scrollActiveTabIntoView() {
      const tabs = this.nodes.tabs;
      const activeTab = tabs?.querySelector('.product-center__tab.is-active');
      if (!tabs || !activeTab) return;
      window.requestAnimationFrame(() => {
        // 只滚动 tabs 容器本身，避免 scrollIntoView 波及页面级滚动容器
        const tabRect = activeTab.getBoundingClientRect();
        const boxRect = tabs.getBoundingClientRect();
        if (tabRect.left < boxRect.left) {
          tabs.scrollLeft += tabRect.left - boxRect.left;
        } else if (tabRect.right > boxRect.right) {
          tabs.scrollLeft += tabRect.right - boxRect.right;
        }
      });
    }

    async loadProducts() {
      if (!this.storeDomain || !this.storefrontToken) {
        this.setStatus('Configure Storefront API settings to load products.');
        this.products = [];
        this.applyFilters();
        return;
      }

      // 全店目录已在内存时,切 tab / 搜索 / 清搜索不再发请求,直接本地过滤
      // (products 可能已被搜索结果覆盖,全店数据始终以 fullCatalog 为准)
      if (this.fullCatalogLoaded) {
        this.products = this.fullCatalog;
        this.applyFilters();
        return;
      }

      const requestId = this.requestId + 1;
      this.requestId = requestId;
      this.isLoadingProducts = true;
      this.products = [];
      this.filteredProducts = [];
      this.renderProducts();
      this.setStatus(this.config.loadingText || 'Loading products...');

      try {
        // 搜索也统一走全量目录 + 本地子串过滤(matchesSearch):Shopify search
        // 引擎只做整词/前缀匹配,搜 "3566" 无法命中元字段里的 "RK3566" token,
        // 服务端永远不会把它查回来,只有本地子串匹配能覆盖这种场景
        this.products = await this.fetchAllProducts('all');
        if (requestId !== this.requestId) return;
        this.isLoadingProducts = false;
        // 全店目录单独存,搜索不覆盖
        this.fullCatalogLoaded = true;
        this.fullCatalog = this.products;
        this.applyFilters();
        this.scrollToGroupFromUrlState();
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.isLoadingProducts = false;
        this.products = [];
        this.applyFilters();
        this.setStatus(`Failed to load products: ${this.cleanValue(error?.message) || 'Unknown error'}`);
        // 失败也要把 URL 定位消费掉(此刻无区块可定位,返回 false 即可):
        // 否则 spy 的 pc_category 同步一直被"未消费"挡住,滚动高亮不再写 URL
        this.scrollToGroupFromUrlState();
      }
    }

    async loadMetaobjectFilters() {
      if (!this.storeDomain || !this.storefrontToken || this.metaobjectFilterGroupConfigs.length === 0) {
        return;
      }

      try {
        const results = await Promise.allSettled(this.metaobjectFilterGroupConfigs.map((group) => this.fetchMetaobjectFilterGroup(group)));
        const groups = results
          .filter((result) => result.status === 'fulfilled')
          .map((result) => result.value);
        const failed = results.filter((result) => result.status === 'rejected');
        if (failed.length) {
          console.warn('Some product center metaobject filters failed to load:', failed.map((result) => result.reason));
        }
        this.filterGroups = [
          ...groups.filter((group) => group.options.length > 0),
          ...this.manualFilterGroups
        ];
        this.reconcileSystemOnChipSelection();
        this.renderFilters();
      } catch (error) {
        console.warn('Failed to load product center metaobject filters:', error);
      }
    }

    async fetchMetaobjectFilterGroup(group) {
      const options = [];
      const seen = new Set();
      let hasNextPage = true;
      let after = null;

      while (hasNextPage) {
        const payload = await this.storefrontRequest(this.metaobjectsQuery(), {
          type: group.type,
          first: 250,
          after
        });
        const connection = payload?.data?.metaobjects;
        const edges = connection?.edges || [];

        edges.forEach((edge) => {
          const option = this.metaobjectOptionFromNode(edge?.node, group);
          if (!option) return;
          const key = option.key || this.normalize(option.value);
          if (!key || seen.has(key)) return;
          seen.add(key);
          options.push(option);
        });

        hasNextPage = Boolean(connection?.pageInfo?.hasNextPage);
        after = connection?.pageInfo?.endCursor || null;
      }

      options.sort((a, b) => {
        if (a.sort !== b.sort) return a.sort - b.sort;
        return a.label.localeCompare(b.label, undefined, { numeric: true });
      });

      return {
        label: group.label,
        key: group.key,
        type: group.type,
        options
      };
    }

    async fetchAllProducts(collectionHandle) {
      const products = [];
      const pageSize = Number(this.config.fetchPageSize) || 100;
      const maxProducts = Number(this.config.maxProducts) || 0;
      let after = null;
      let hasNextPage = true;
      let useRootProducts = !collectionHandle || this.normalize(collectionHandle) === 'all';

      while (hasNextPage) {
        const variables = {
          first: Math.min(pageSize, 250),
          after
        };
        if (!useRootProducts) variables.handle = collectionHandle;

        const payload = await this.storefrontRequest(useRootProducts ? this.productsQuery() : this.collectionProductsQuery(), variables);

        if (!useRootProducts && !payload?.data?.collection) {
          useRootProducts = true;
          after = null;
          products.length = 0;
          continue;
        }

        const productConnection = useRootProducts ? payload?.data?.products : payload?.data?.collection?.products;
        const edges = productConnection?.edges || [];
        edges.forEach((edge) => {
          const product = this.productFromNode(edge?.node);
          if (product) products.push(product);
        });

        hasNextPage = Boolean(productConnection?.pageInfo?.hasNextPage);
        after = productConnection?.pageInfo?.endCursor || null;

        if (maxProducts > 0 && products.length >= maxProducts) {
          return products.slice(0, maxProducts);
        }
      }

      return products;
    }

    async fetchSearchProducts(term) {
      const products = [];
      const pageSize = Number(this.config.fetchPageSize) || 100;
      const maxProducts = Number(this.config.maxProducts) || 0;
      const query = this.cleanValue(term);
      let after = null;
      let hasNextPage = Boolean(query);

      while (hasNextPage) {
        const payload = await this.storefrontRequest(this.productSearchQuery(), {
          first: Math.min(pageSize, 250),
          after,
          query
        });
        const productConnection = payload?.data?.search;
        const edges = productConnection?.edges || [];
        edges.forEach((edge) => {
          // search 是 Product/Page/Article 混合 union,非产品节点丢弃
          if (edge?.node?.__typename && edge.node.__typename !== 'Product') return;
          const product = this.productFromNode(edge?.node);
          if (product) products.push(product);
        });

        hasNextPage = Boolean(productConnection?.pageInfo?.hasNextPage);
        after = productConnection?.pageInfo?.endCursor || null;

        if (maxProducts > 0 && products.length >= maxProducts) {
          return products.slice(0, maxProducts);
        }
      }

      return products;
    }

    applyFilters() {
      // 一页渲染模式:筛选只做搜索/checkbox 过滤;大分类由 renderProducts 按系列分区呈现
      this.filteredProducts = this.products.filter((product) => {
        if (this.hasSearchTerm() && !this.matchesSearch(product)) return false;
        return this.matchesSelectedFilters(product);
      });
      this.updateResetCount();
      this.renderTabs();
      this.renderProducts();
    }

    // 判断产品是否属于某个大分类(按后台真实的系列产品关系,
    // 不依赖 productType/tags 手动字段;分类未绑定集合时才回落到字段模糊匹配)
    matchesCategoryFor(product, category) {
      const collectionHandle = this.cleanValue(category?.collectionHandle);
      if (collectionHandle) {
        return Array.isArray(product.collections) && product.collections.includes(collectionHandle);
      }
      const target = this.normalize(category?.value || category?.label || '');
      if (!target) return true;
      const values = [
        product.productType,
        product.resourceCategory,
        product.category,
        ...(product.tags || [])
      ].map((item) => this.normalize(item)).filter(Boolean);
      return values.some((value) => value === target || value.includes(target) || target.includes(value));
    }

    // 大分类 handle → 区块 DOM id 片段(渲染与 tab 点击/URL 定位共用同一规则)
    categoryBlockToken(category) {
      const handle = this.cleanValue(category?.value || category?.collectionHandle || category?.label);
      return this.normalize(handle).replace(/\s+/g, '-') || 'block';
    }

    // 当前筛选下该大分类是否还有产品(筛选后隐藏空大类的 tab,与区块过滤同一匹配规则)
    categoryHasProducts(category) {
      return this.filteredProducts.some((product) => this.matchesCategoryFor(product, category));
    }

    getActiveCategory() {
      return this.categories.find((category) => {
        const value = category.value || category.collectionHandle || category.label;
        return value === this.activeCategory;
      }) || null;
    }

    getActiveCollectionHandle() {
      return this.cleanValue(this.getActiveCategory()?.collectionHandle) || this.fallbackCollectionHandle;
    }

    matchesSearch(product) {
      const term = this.normalize(this.searchTerm);
      if (!term) return true;
      const haystack = [
        product.title,
        product.productType,
        product.vendor,
        product.resourceCategory,
        product.chip_platform,
        product.system_on_chip,
        product.soc,
        product.chip_type,
        ...this.metafieldRefs.map((ref) => product[ref.canonical]),
        ...(product.tags || [])
      ].join(' ');
      return this.normalize(haystack).includes(term);
    }

    matchesTitleSearch(product) {
      const term = this.normalize(this.searchTerm);
      if (!term) return true;
      return this.normalize(product?.title).includes(term);
    }

    hasSearchTerm() {
      return Boolean(this.cleanValue(this.searchTerm));
    }

    matchesSelectedFilters(product) {
      for (const [key, selectedValues] of this.selectedFilters.entries()) {
        const productValue = this.normalize(this.getProductValue(product, key));
        if (!productValue) return false;
        const hasMatch = Array.from(selectedValues).some((selected) => {
          const normalized = this.normalize(selected);
          return productValue === normalized || productValue.includes(normalized) || normalized.includes(productValue);
        });
        if (!hasMatch) return false;
      }
      return true;
    }

    // 状态行：搜索时按 Figma（27313-2264）在结果数下方渲染关键字 chip
    // （橙边框圆角 + 弯引号关键字 + × 清除按钮，点击 × 等同清空搜索框）
    renderSummary(total) {
      if (!this.nodes.summary) return;
      const term = this.hasSearchTerm() ? this.searchTerm : '';
      const text = term
        ? `Search results: ${total} products`
        : `Total：${total} products`;
      const chipMarkup = term
        ? `<button class="product-center__search-chip" type="button" data-product-center-search-clear aria-label="Clear search">
            <span class="product-center__search-chip-term">“${this.escapeHtml(term)}”</span>
            <svg class="product-center__search-chip-icon" width="7" height="7" viewBox="0 0 7 7" fill="none" aria-hidden="true">
              <path d="M0.5 0.5L3.5 3.5L6.5 0.5" stroke="#FF6600" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M0.5 6.5L3.5 3.5L6.5 6.5" stroke="#FF6600" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>`
        : '';
      this.nodes.summary.innerHTML = `<span class="product-center__summary-text">${this.escapeHtml(text)}</span>${chipMarkup}`;
    }

    renderProducts() {
      const total = this.filteredProducts.length;

      this.renderSummary(total);

      if (!this.products.length) {
        this.catBlocks = [];
        this.nodes.grid.innerHTML = '';
        this.renderGroupsNav([]);
        this.syncFilterPanelHeight();
        if (!this.nodes.status.textContent) this.setStatus(this.config.emptyText || 'No products match the selected filters.');
        return;
      }

      this.setStatus(total ? '' : (this.config.emptyText || 'No products match the selected filters.'));

      // 一页渲染:每个大分类一个区块(按产品所属系列过滤),滚动 spy 自动切换 tab 高亮。
      // 搜索结果同样分区呈现(搜索查询与全店查询共用 productFields,产品带 collections)
      // 组 id 带区块前缀,避免同名 subcategory 跨区块的 DOM id 冲突
      this.catBlocks = this.categories.map((category) => {
        const token = this.categoryBlockToken(category);
        const list = this.filteredProducts.filter((product) => this.matchesCategoryFor(product, category));
        const groups = this.groupFilteredProducts(list).map((group) => ({
          ...group,
          id: `pc-${token}-group-${String(group.id).replace(/^pc-group-/, '')}`
        }));
        return { category, token, groups };
      }).filter((block) => block.groups.length > 0);

      this.nodes.grid.innerHTML = this.catBlocks.map((block) => `
        <section class="product-center__catblock" id="pc-cat-${this.escapeHtml(block.token)}" data-product-center-catblock data-category-token="${this.escapeHtml(block.token)}">
          ${block.groups.map((group) => this.renderGroupSection(group, block.category)).join('')}
        </section>
      `).join('');
      this.syncCompareInputs();
      this.renderGroupsNavPills(this.catBlocks[0]?.groups || []);
      this.setupPageSpy();
      this.syncFilterPanelHeight();
    }

    groupFilteredProducts(products) {
      // 搜索结果与普通态一致:同样按归类字段(subcategory)分组
      const map = new Map();
      products.forEach((product) => {
        const value = this.cleanValue(this.getProductValue(product, this.groupField));
        const key = this.normalize(value).replace(/\s+/g, '-');
        const mapKey = key || '__empty__';
        if (!map.has(mapKey)) {
          map.set(mapKey, {
            id: `pc-group-${key || 'others'}`,
            label: value || 'Others',
            value,
            products: []
          });
        }
        map.get(mapKey).products.push(product);
      });
      return Array.from(map.values());
    }

    renderGroupSection(group, category) {
      const rows = group.products.map((product) => this.renderProductRow(product, category)).join('');
      return `
        <section class="product-center__grouptable" id="${this.escapeHtml(group.id)}" data-product-center-grouptable>
          <h2 class="product-center__grouptable-title">
            <button class="product-center__grouptable-toggle" type="button" data-product-center-group-toggle aria-expanded="true">
              <span class="product-center__grouptable-label">${this.escapeHtml(group.label)}</span>
              <span class="product-center__grouptable-count">${group.products.length}</span>
            </button>
          </h2>
          <div class="product-center__table" role="table" aria-label="${this.escapeHtml(group.label)}">
            <div class="product-center__table-head" role="row">
              <span class="product-center__table-hcell product-center__table-hcell--product" role="columnheader">Product</span>
              ${this.tableColumns.map((column) => `<span class="product-center__table-hcell" role="columnheader">${this.escapeHtml(column.label)}</span>`).join('')}
              <span class="product-center__table-hcell product-center__table-hcell--actions" role="columnheader">Others</span>
            </div>
            ${rows}
          </div>
          ${group.products.length ? '' : '<p class="product-center__grouptable-empty">No products in this group.</p>'}
        </section>
      `;
    }

    renderProductRow(product, category) {
      const image = product.image?.url
        ? `<img src="${this.escapeHtml(product.image.url)}" alt="${this.escapeHtml(product.image.altText || product.title)}" loading="lazy">`
        : '<div class="product-center__product-placeholder" aria-hidden="true"></div>';
      const href = product.onlineStoreUrl || `/products/${product.handle}`;
      const title = this.getProductValue(product, this.cardTitleField) || product.title;
      const imageUrl = product.image?.url || '';
      const checked = this.isCompareSelected(product.handle) ? ' checked' : '';
      // 无规格书不再兜底跳产品页：Datasheet 置灰不可点（见渲染处的 --disabled 分支）
      const datasheetHref = this.getProductDatasheetHref(product);
      const buyLabel = this.config.buyLabel || 'Buy Now';
      // 一页渲染下按区块判定:Accessories 区块内置灰,其他区块正常可勾
      const compareDisabled = this.isCompareDisabledByCategory(category);

      const specCells = this.tableColumns.map((column) => {
        // 分号分段的堆叠组各占一行（如 SoC 名下方堆 NPU 算力）；组内逗号字段仍是
        // 兼容备选（纯文本/元对象/富文本等不同存法），按顺序只取第一个有值，避免重复堆叠；
        // 无值的组整行跳过（如产品没填 NPU 就只显示 SoC 名），全空才显示 —
        const values = (column.groups || [column.fields])
          .map((fields) => this.cleanValue(this.getFirstProductValue(product, fields)))
          .filter(Boolean);
        const linesMarkup = values.length
          ? values.map((value) => `<span class="product-center__cell-line">${this.escapeHtml(value)}</span>`).join('')
          : '<span class="product-center__cell-line product-center__cell-line--empty">—</span>';
        // 手机端没有表头，行内补字段名标签让参数可读（桌面隐藏，由 sticky 表头承担）；
        // 空值列整格隐藏，避免手机端只露出光秃秃的字段名。
        // 内容过长时格子内最多显示 5 行（CSS 截断出省略号），完整内容放原生 title 悬停查看
        return `<div class="product-center__cell${values.length ? '' : ' is-empty'}" role="cell"${values.length ? ` title="${this.escapeHtml(values.join('\n'))}"` : ''}><span class="product-center__cell-label">${this.escapeHtml(column.label)}</span>${linesMarkup}</div>`;
      }).join('');

      return `
        <div class="product-center__row" role="row" data-product-title="${this.escapeHtml(product.title)}">
          <div class="product-center__cell product-center__cell--product" role="cell">
            <a class="product-center__row-media" href="${this.escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${image}</a>
            <a class="product-center__row-title" href="${this.escapeHtml(href)}" target="_blank" rel="noopener noreferrer" title="${this.escapeHtml(title)}">${this.escapeHtml(title)}</a>
          </div>
          ${specCells}
          <div class="product-center__cell product-center__cell--actions" role="cell">
            <label class="product-center__row-action product-center__row-action--compare${compareDisabled ? ' product-center__row-action--disabled' : checked ? ' is-checked' : ''}"${compareDisabled ? ' aria-disabled="true"' : ''}>
              <input
                class="product-center__row-check"
                type="checkbox"
                aria-label="Select ${this.escapeHtml(product.title)}"
                data-product-center-compare-input
                data-product-handle="${this.escapeHtml(product.handle)}"
                data-product-title="${this.escapeHtml(title)}"
                data-product-image="${this.escapeHtml(imageUrl)}"${checked}${compareDisabled ? ' disabled' : ''}>
              ${this.iconMarkup('compare')}
              <span class="product-center__row-action-text">Compare</span>
            </label>
            ${
              datasheetHref
                ? `<a class="product-center__row-action" href="${this.escapeHtml(datasheetHref)}" target="_blank" rel="noopener noreferrer">
                    ${this.iconMarkup('datasheet')}
                    <span class="product-center__row-action-text">Manual</span>
                  </a>`
                : `<span class="product-center__row-action product-center__row-action--disabled" aria-disabled="true">
                    ${this.iconMarkup('datasheet')}
                    <span class="product-center__row-action-text">Manual</span>
                  </span>`
            }
            <a class="product-center__row-action" href="${this.escapeHtml(href)}" target="_blank" rel="noopener noreferrer">
              ${this.iconMarkup('buy')}
              <span class="product-center__row-action-text">${this.escapeHtml(buyLabel)}</span>
            </a>
          </div>
        </div>
      `;
    }

    // 组 pill 锚点条:只负责渲染,监听由 setupPageSpy 统一接管
    // (一页渲染下滚动跨区块时整条切换内容,不能每次重建都拆挂监听)
    renderGroupsNavPills(groups) {
      if (!this.nodes.groupsNav) return;
      // 单个分组也显示：缺位会让冻结链（分类条/锚点条/表头）中间空一段，
      // 滚动时内容从缺口处露出(搜索结果分区后同样显示锚点条)
      const visible = groups.length > 0;
      this.nodes.groupsNav.hidden = !visible;
      if (!visible) {
        this.nodes.groupsNav.innerHTML = '';
        return;
      }

      this.nodes.groupsNav.innerHTML = groups.map((group, index) => `
        <button class="product-center__groups-nav-item${index === 0 ? ' is-active' : ''}" type="button" data-product-center-group-nav="${this.escapeHtml(group.id)}">${this.escapeHtml(group.label)}</button>
      `).join('');
      window.requestAnimationFrame(() => this.updateGroupsNavOverflow());
    }

    renderGroupsNav(groups) {
      this.renderGroupsNavPills(groups);
      if (this.nodes.groupsNav && !this.nodes.groupsNav.hidden) {
        this.setupGroupsSpy(groups);
      }
    }

    // 一页渲染的统一滚动 spy:一个监听同时负责——
    // 1) 大分类区块过线 → 切换顶部 tab 高亮 + 同步 URL(replace,不打断滚动);
    // 2) 滚进新区块时,组 pill 锚点条整条切换为该区块的 subcategory;
    // 3) 区块内 subcategory 过线 → 高亮对应 pill。
    // 探测线规则与 setupGroupsSpy 同源(锚点落点 +150),scroll + rAF 节流,
    // window 与内部滚动容器双监听(disconnect 由 destroyGroupsSpy 统一清理)
    setupPageSpy() {
      this.groupsNavSourceIds = '';
      this.destroyGroupsSpy();

      const catSections = Array.from(this.querySelectorAll('[data-product-center-catblock]'));
      // 搜索结果同样分区呈现,spy 照常接管(无区块时才跳过)
      if (!catSections.length) return;

      const activeLine = () => {
        const margin = parseFloat(getComputedStyle(catSections[0]).scrollMarginTop) || 0;
        return margin + 150;
      };

      const nearBottom = () => {
        const container = this.groupsSpyScrollContainer;
        const doc = document.documentElement;
        return container
          ? container.scrollTop + container.clientHeight >= container.scrollHeight - 8
          : window.innerHeight + window.scrollY >= doc.scrollHeight - 8;
      };

      // 过线者中取最靠下(最后一个吸顶的);无过线时已滚到底取最后一个,否则取第一个
      const pickByLine = (sections, line) => {
        let passed = null;
        let passedTop = -Infinity;
        sections.forEach((section) => {
          const top = section.getBoundingClientRect().top;
          if (top <= line && top > passedTop) {
            passed = section;
            passedTop = top;
          }
        });
        if (passed) return passed;
        return nearBottom() ? sections[sections.length - 1] : sections[0];
      };

      const applyActive = () => {
        const line = activeLine();

        // 1) 当前大分类区块
        const blockSection = pickByLine(catSections, line);
        const token = blockSection?.dataset.categoryToken || '';
        if (token && token !== this.activeBlockToken()) {
          const category = this.categories.find((item) => this.categoryBlockToken(item) === token);
          this.activeCategory = category?.value || category?.collectionHandle || category?.label || token;
          this.renderTabs();
          // URL 携带的大类/分组定位尚未消费时,不得把视口所在区块写回 pc_category:
          // 首次进页/菜单软切换后,产品尚未加载完成,视口先停在第一个区块,
          // 此时同步会把深链的 pc_category 覆盖成第一个大类,加载完成后
          // scrollToGroupFromUrlState 读到的就是被覆盖的参数,定位丢失
          if (
            this.groupAnchorConsumed ||
            !(this.cleanValue(this.urlState?.category) || this.cleanValue(this.urlState?.group))
          ) {
            this.syncProductCenterUrlState({ replace: true });
          }
        }

        // 2) 组 pill 行随区块切换(内容有变才重建)
        const block = this.catBlocks.find((item) => item.token === token) || this.catBlocks[0];
        const groups = block?.groups || [];
        const sourceIds = groups.map((group) => group.id).join('|');
        if (sourceIds !== this.groupsNavSourceIds) {
          this.groupsNavSourceIds = sourceIds;
          this.renderGroupsNavPills(groups);
        }

        // 3) 区块内 subcategory 高亮
        const sections = groups
          .map((group) => this.querySelector(`#${CSS.escape(group.id)}`))
          .filter(Boolean);
        if (!sections.length) return;
        const bestId = pickByLine(sections, line).id;
        let activePill = null;
        this.querySelectorAll('[data-product-center-group-nav]').forEach((item) => {
          const isActive = item.dataset.productCenterGroupNav === bestId;
          item.classList.toggle('is-active', isActive);
          if (isActive) activePill = item;
        });
        this.scrollActiveGroupPillIntoView(activePill);
      };

      const onScroll = () => {
        if (this.groupsSpyRaf) return;
        this.groupsSpyRaf = window.requestAnimationFrame(() => {
          this.groupsSpyRaf = null;
          applyActive();
        });
      };
      this.groupsSpyScrollHandler = onScroll;
      this.groupsSpyScrollContainer = this.getScrollContainer();
      window.addEventListener('scroll', onScroll, { passive: true });
      if (this.groupsSpyScrollContainer) {
        this.groupsSpyScrollContainer.addEventListener('scroll', onScroll, { passive: true });
      }
      applyActive();
    }

    activeBlockToken() {
      return this.categoryBlockToken(this.getActiveCategory());
    }

    // 滚动高亮切换后，若当前高亮的 pill 被横向挤出锚点条可视区（分组多、
    // 条宽超出一行时），最小幅度横滚使其完整露出——分组多时页面滚动切换
    // 高亮，pill 却停在视野外，用户看不到当前组是谁。
    // 只在不完整可见时介入，不与用户手动横滚抢滚动条；不能用 scrollIntoView：
    // 它会波及页面级滚动容器（见 scrollToGroupSection 注释），故手算 scrollLeft。
    // 横滚后渐隐/滚动条状态由 nav 自身 scroll 事件 → updateGroupsNavOverflow 同步
    scrollActiveGroupPillIntoView(pill) {
      const nav = this.nodes.groupsNav;
      if (!pill || !nav || nav.hidden) return;
      const maxScroll = nav.scrollWidth - nav.clientWidth;
      if (maxScroll <= 1) return;
      const PAD = 12;
      const navRect = nav.getBoundingClientRect();
      const pillRect = pill.getBoundingClientRect();
      const fullyVisible = pillRect.left >= navRect.left + PAD && pillRect.right <= navRect.right - PAD;
      if (fullyVisible) return;
      const delta = pillRect.left < navRect.left + PAD
        ? pillRect.left - navRect.left - PAD
        : pillRect.right - navRect.right + PAD;
      nav.scrollTo({ left: Math.max(0, Math.min(maxScroll, nav.scrollLeft + delta)), behavior: 'smooth' });
    }

    // PC 鼠标没有横向滚动手段（滚动条隐藏），把纵向滚轮映射为锚点条横滚；
    // 已到条内边缘时放行事件，让页面继续正常纵向滚动
    scrollGroupsNavByWheel(event) {
      const nav = this.nodes.groupsNav;
      if (!nav || nav.hidden || !event.deltaY) return;
      const maxScroll = nav.scrollWidth - nav.clientWidth;
      if (maxScroll <= 1) return;
      const atStart = nav.scrollLeft <= 0 && event.deltaY < 0;
      const atEnd = nav.scrollLeft >= maxScroll - 1 && event.deltaY > 0;
      if (atStart || atEnd) return;
      event.preventDefault();
      // deltaMode：1=行（Firefox，一格≈40px），2=页（按容器宽计），0=像素
      const delta = event.deltaMode === 1 ? event.deltaY * 40
        : event.deltaMode === 2 ? event.deltaY * nav.clientWidth
          : event.deltaY;
      nav.scrollLeft = Math.max(0, Math.min(maxScroll, nav.scrollLeft + delta));
      // 不依赖 scroll 事件回调，入口处主动同步 thumb 与渐隐状态
      this.updateGroupsNavOverflow();
    }

    // 溢出渐隐提示状态：左缘已滚过/右缘未滚到底时分别点亮对应侧渐隐，滚到头自动消失；
    // 顺带同步 overlay 滚动条几何（scroll/wheel/render/resize 都汇到这里）
    updateGroupsNavOverflow() {
      const nav = this.nodes.groupsNav;
      if (!nav || nav.hidden) return;
      const maxScroll = nav.scrollWidth - nav.clientWidth;
      nav.classList.toggle('is-overflow-start', nav.scrollLeft > 2);
      nav.classList.toggle('is-overflow-end', maxScroll > 2 && nav.scrollLeft < maxScroll - 2);
    }

    setupGroupsSpy(groups) {
      const sections = groups
        .map((group) => this.querySelector(`#${CSS.escape(group.id)}`))
        .filter(Boolean);
      if (!sections.length) return;

      // 高亮判定用“比例探测线”：视口（或内部滚动容器可视区）上部 40% 处。
      // 组标题进入视口上部 40% 即视为当前组。不依赖任何写死的像素值——
      // header 实际高度、冻结链高度、断点差异都不会让它漂移
      // （此前用固定像素线，但实际环境的 header 比 CSS 假设的高出几十像素，
      // 线忽高忽低，表现为高亮切错组或完全不切）。
      // 再与锚点落点（scroll-margin-top）+4 取大者：点击 pill / URL 跳转
      // 会把标题停在落点处，取大者保证落定后目标组必然过线、高亮必切
      const activeLine = () => {
        // 判定线锚定“锚点落点”（scroll-margin-top，动态读取）：标题爬到
        // 落点下方 150px（约一个表头 + 首行产品的高度）即接管为当前组。
        // 线与视口高度完全无关——矮组连排时相邻组标题间距只有两三百像素，
        // 任何随视口放大的比例线在大屏上都会把下一组标题也罩进判定区，
        // 高亮被更靠下的组抢走（“明明 Expansion 在上面却高亮 Camera”）。
        // 锚定落点后：点击 pill / URL 跳转把标题停在落点处，必然过线；
        // 切换瞬间上一组标题恰好刚被冻结链吞没，与视觉一致
        const margin = parseFloat(getComputedStyle(sections[0]).scrollMarginTop) || 0;
        return margin + 150;
      };

      // 全量重算（分组数量少，代价可忽略）。选中规则：
      // 1. 标题已越过高亮线的组中取最靠下的——即最后一个吸顶的组；
      // 2. 没有组过线：已滚到底取最后一组（矮尾组标题永远到不了线），
      //    否则取第一组（页面顶部，首组标题还在线下方）
      const applyActive = () => {
        const line = activeLine();
        // 过线组中取最靠下（top 最大）的。注意不能用 passed.top 比较——
        // DOM 元素没有 .top 属性（恒 undefined），比较恒 false 会把选中
        // 冻结在第一个过线的组，表现为高亮永远钉死第一组
        let passed = null;
        let passedTop = -Infinity;
        sections.forEach((section) => {
          const top = section.getBoundingClientRect().top;
          if (top <= line && top > passedTop) {
            passed = section;
            passedTop = top;
          }
        });

        let bestId;
        if (passed) {
          bestId = passed.id;
        } else {
          const container = this.groupsSpyScrollContainer;
          const doc = document.documentElement;
          const nearBottom = container
            ? container.scrollTop + container.clientHeight >= container.scrollHeight - 8
            : window.innerHeight + window.scrollY >= doc.scrollHeight - 8;
          bestId = (nearBottom ? sections[sections.length - 1] : sections[0]).id;
        }

        let activePill = null;
        this.querySelectorAll('[data-product-center-group-nav]').forEach((item) => {
          const isActive = item.dataset.productCenterGroupNav === bestId;
          item.classList.toggle('is-active', isActive);
          if (isActive) activePill = item;
        });
        this.scrollActiveGroupPillIntoView(activePill);
      };

      // scroll 驱动 + rAF 节流。不能用 IntersectionObserver：组在判定带内滚动时
      // 交叉状态不变、过线瞬间不触发回调，高亮会错停在上一组。
      // 页面可能滚在内部容器（见 getScrollContainer），window 和容器都要监听
      const onScroll = () => {
        if (this.groupsSpyRaf) return;
        this.groupsSpyRaf = window.requestAnimationFrame(() => {
          this.groupsSpyRaf = null;
          applyActive();
        });
      };
      this.groupsSpyScrollHandler = onScroll;
      this.groupsSpyScrollContainer = this.getScrollContainer();
      window.addEventListener('scroll', onScroll, { passive: true });
      if (this.groupsSpyScrollContainer) {
        this.groupsSpyScrollContainer.addEventListener('scroll', onScroll, { passive: true });
      }
      applyActive();
    }

    destroyGroupsSpy() {
      this.groupsSpy = null;
      if (this.groupsSpyScrollHandler) {
        window.removeEventListener('scroll', this.groupsSpyScrollHandler);
        if (this.groupsSpyScrollContainer) {
          this.groupsSpyScrollContainer.removeEventListener('scroll', this.groupsSpyScrollHandler);
        }
        this.groupsSpyScrollHandler = null;
        this.groupsSpyScrollContainer = null;
      }
      if (this.groupsSpyRaf) {
        window.cancelAnimationFrame(this.groupsSpyRaf);
        this.groupsSpyRaf = null;
      }
    }

    getScrollContainer() {
      let el = this.parentElement;
      while (el && el !== document.documentElement) {
        const overflowY = getComputedStyle(el).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    }

    scrollToGroupSection(section) {
      if (!section) return;
      const margin = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
      const container = this.getScrollContainer();
      if (container) {
        // 主题可能使用内部滚动容器（如 overflow:auto 的 page-wrapper），
        // 不能用 scrollIntoView：它会把 overflow:hidden 的 html 层也滚走，导致整页错位和底部空白
        const top = section.getBoundingClientRect().top
          - container.getBoundingClientRect().top
          + container.scrollTop
          - margin;
        container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return;
      }
      const top = section.getBoundingClientRect().top + window.scrollY - margin;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    // URL 携带 pc_group=分组名 时（header Products 菜单“分类-子分类”条目跳转用），
    // 产品渲染完成后自动定位到对应 subcategory 分组；只带 pc_category 的大类条目
    // 则定位到对应大类区块——一页渲染后全部分区常驻 DOM，activeCategory 只管
    // tab 高亮，不会让视口落在该大类（旧 tab 切换渲染模式只渲染当前大类，
    // 视觉上等效定位，该能力必须在渲染后用滚动补回）。
    // 分组 id 与 renderProducts 的生成规则一致：pc-<区块 token>-group-<归一化分组名>；
    // 搜索结果同样分区，匹配不到时静默忽略。仅消费一次，后续筛选交互不再触发。
    // 返回是否真正发起了定位（无参数 / 已消费 / 目标不存在时为 false，
    // 调用方据此决定是否回落到产品中心顶部）
    scrollToGroupFromUrlState() {
      if (this.groupAnchorConsumed) return false;
      const anchor = this.cleanValue(this.urlState?.group);
      const categoryParam = this.cleanValue(this.urlState?.category);
      if (!anchor && !categoryParam) return false;
      this.groupAnchorConsumed = true;
      const catToken = this.normalize(categoryParam).replace(/\s+/g, '-');

      // 1) 子分类分组：组 id 带区块前缀(pc-<block>-group-<key>)，
      //    URL 同时带 pc_category 时优先定该区块的组，否则取同名第一组
      if (anchor) {
        const groupKey = this.normalize(anchor).replace(/\s+/g, '-');
        let section = catToken
          ? this.querySelector(`#pc-${CSS.escape(catToken)}-group-${CSS.escape(groupKey)}`)
          : null;
        if (!section) {
          section = this.querySelector(`[data-product-center-grouptable][id$="-group-${CSS.escape(groupKey)}"]`);
        }
        if (section) {
          this.scrollToGroupSection(section);
          return true;
        }
      }

      // 2) 大类区块：pc-cat-<token>，token 规则与 categoryBlockToken 一致
      const block = catToken ? this.querySelector(`#pc-cat-${CSS.escape(catToken)}`) : null;
      if (block) {
        this.scrollToGroupSection(block);
        return true;
      }
      return false;
    }

    iconMarkup(name) {
      const svg = this.icons?.[name];
      return svg ? `<span class="product-center__row-icon">${svg}</span>` : '';
    }

    // datasheet 字段三种存法都支持：file_reference（URL 在 reference，value 只是 gid）、
    // 纯 URL 文本、link 类型（value 是 {text,url} JSON，经 stringifyMetafieldJson
    // 拼接后 URL 混在文本里，需再提取）；详情页 liquid 用 .value.url | default: .value 消费同一字段
    getProductDatasheetHref(product) {
      if (!this.datasheetField) return '';
      const ref = this.normalizeMetafieldRef(this.datasheetField);
      const fileUrl = this.cleanValue(product[`fileUrl:${ref.canonical}`]);
      if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
      const value = this.cleanValue(this.getProductValue(product, this.datasheetField));
      if (/^https?:\/\//i.test(value)) return value;
      const embedded = value.match(/https?:\/\/[^\s"',]+/i);
      return embedded ? embedded[0] : '';
    }

    metafieldReferenceUrl(field) {
      const reference = field?.reference;
      if (!reference) return '';
      return this.cleanValue(reference.url) || this.cleanValue(reference.image?.url);
    }

    syncFilterPanelHeight() {
      if (!this.nodes.filterPanel || !this.nodes.grid || window.innerWidth < 768) {
        this.style.removeProperty('--product-center-filter-height');
        this.style.removeProperty('--product-center-filter-groups-height');
        return;
      }

      window.requestAnimationFrame(() => {
        const filterRect = this.nodes.filterPanel.getBoundingClientRect();
        const gridRect = this.nodes.grid.getBoundingClientRect();
        const groupsRect = this.nodes.filters?.getBoundingClientRect();
        const minFilterHeight = 1231;
        // 桌面（≥990）筛选栏 sticky 吸附滚动，高度收进视口（视口-header-14px 对齐偏移-底距 24），
        // 不再与产品区等高——等高会让面板随产品长度滚出视野；
        // 14px 与 CSS 吸附位下移量一致（标题对齐大类 tab 文字），保证面板底不超出视口；
        // 平板（768-989）保持原有"与产品区等高"的布局
        const headerH = parseFloat(getComputedStyle(document.body).getPropertyValue('--firefly-header-height')) || 80;
        const height = window.innerWidth >= 990
          ? Math.max(420, window.innerHeight - headerH - 38)
          : Math.max(minFilterHeight, gridRect.bottom - filterRect.top);
        const groupsHeight = groupsRect ? Math.max(0, height - (groupsRect.top - filterRect.top)) : 0;
        this.style.setProperty('--product-center-filter-height', `${Math.ceil(height)}px`);
        if (groupsHeight) {
          this.style.setProperty('--product-center-filter-groups-height', `${Math.ceil(groupsHeight)}px`);
        }
        window.requestAnimationFrame(() => this.updateFilterScrollbar());
      });
    }

    observeFilterPanelHeight() {
      if (!this.nodes.grid || typeof ResizeObserver !== 'function') return;
      this.filterPanelResizeObserver?.disconnect();
      this.filterPanelResizeObserver = new ResizeObserver(() => this.syncFilterPanelHeight());
      this.filterPanelResizeObserver.observe(this.nodes.grid);
    }

    ensureFilterScrollbar() {
      if (!this.nodes.filterPanel || this.nodes.filterScrollbar) return;
      const scrollbar = document.createElement('div');
      scrollbar.className = 'product-center__filter-scrollbar';
      scrollbar.hidden = true;
      scrollbar.innerHTML = '<span class="product-center__filter-scrollbar-thumb" aria-hidden="true"></span>';
      this.nodes.filterPanel.append(scrollbar);
      this.nodes.filterScrollbar = scrollbar;
      this.nodes.filterScrollbarThumb = scrollbar.querySelector('.product-center__filter-scrollbar-thumb');
      scrollbar.addEventListener('pointerdown', (event) => this.onFilterScrollbarPointerDown(event));
    }

    updateFilterScrollbar() {
      const scroller = this.nodes.filters;
      const scrollbar = this.nodes.filterScrollbar;
      const thumb = this.nodes.filterScrollbarThumb;
      if (!scroller || !scrollbar || !thumb || window.innerWidth < 768) {
        if (scrollbar) scrollbar.hidden = true;
        return;
      }

      const scrollHeight = scroller.scrollHeight;
      const clientHeight = scroller.clientHeight;
      if (scrollHeight <= clientHeight + 1) {
        scrollbar.hidden = true;
        return;
      }

      scrollbar.hidden = false;
      const trackHeight = scrollbar.clientHeight;
      const thumbHeight = Math.max(28, Math.round((clientHeight / scrollHeight) * trackHeight));
      const maxScroll = scrollHeight - clientHeight;
      const maxY = Math.max(0, trackHeight - thumbHeight);
      const thumbY = maxScroll > 0 ? Math.round((scroller.scrollTop / maxScroll) * maxY) : 0;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.setProperty('--product-center-filter-thumb-y', `${thumbY}px`);
    }

    onFilterScrollbarPointerDown(event) {
      const scroller = this.nodes.filters;
      const scrollbar = this.nodes.filterScrollbar;
      const thumb = this.nodes.filterScrollbarThumb;
      if (!scroller || !scrollbar || !thumb || scrollbar.hidden) return;

      event.preventDefault();
      const trackRect = scrollbar.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      const isThumb = event.target === thumb;

      if (!isThumb) {
        this.scrollFilterGroupsToTrackPosition(event.clientY - trackRect.top);
      }

      this.filterScrollbarDrag = {
        startY: event.clientY,
        startScrollTop: scroller.scrollTop,
        trackHeight: scrollbar.clientHeight,
        thumbHeight: thumb.offsetHeight
      };

      window.addEventListener('pointermove', this.handleFilterScrollbarPointerMove);
      window.addEventListener('pointerup', this.handleFilterScrollbarPointerUp, { once: true });

      if (isThumb && thumb.setPointerCapture) {
        try {
          thumb.setPointerCapture(event.pointerId);
        } catch (_error) {
          /* no-op */
        }
      }
    }

    onFilterScrollbarPointerMove(event) {
      const scroller = this.nodes.filters;
      if (!scroller || !this.filterScrollbarDrag) return;

      event.preventDefault();
      const maxScroll = scroller.scrollHeight - scroller.clientHeight;
      const maxY = Math.max(1, this.filterScrollbarDrag.trackHeight - this.filterScrollbarDrag.thumbHeight);
      const deltaY = event.clientY - this.filterScrollbarDrag.startY;
      scroller.scrollTop = this.filterScrollbarDrag.startScrollTop + (deltaY / maxY) * maxScroll;
    }

    onFilterScrollbarPointerUp() {
      this.filterScrollbarDrag = null;
      window.removeEventListener('pointermove', this.handleFilterScrollbarPointerMove);
    }

    scrollFilterGroupsToTrackPosition(trackY) {
      const scroller = this.nodes.filters;
      const scrollbar = this.nodes.filterScrollbar;
      const thumb = this.nodes.filterScrollbarThumb;
      if (!scroller || !scrollbar || !thumb) return;

      const maxScroll = scroller.scrollHeight - scroller.clientHeight;
      const maxY = Math.max(1, scrollbar.clientHeight - thumb.offsetHeight);
      const targetY = Math.max(0, Math.min(maxY, trackY - thumb.offsetHeight / 2));
      scroller.scrollTop = (targetY / maxY) * maxScroll;
    }

    updateResetCount() {
      if (!this.nodes.reset) return;
      let count = this.searchTerm ? 1 : 0;
      this.selectedFilters.forEach((values) => { count += values.size; });
      this.nodes.reset.textContent = `Reset（${count}）`;
    }

    // 卡片图优先取产品 metafield custom.thumbnail（兼容 file / 纯 URL 两种存法），
    // 未填时回落产品主图 featuredImage
    resolveProductImage(node) {
      const ref = node.thumbnail?.reference;
      const thumbUrl = ref?.image?.url
        || (ref && typeof ref.url === 'string' ? ref.url : '')
        || (typeof node.thumbnail?.value === 'string' && /^https?:\/\//.test(node.thumbnail.value) ? node.thumbnail.value : '');
      if (thumbUrl) {
        return {
          url: thumbUrl,
          altText: ref?.image?.altText || node.featuredImage?.altText || node.title
        };
      }
      return node.featuredImage || null;
    }

    productFromNode(node) {
      if (!node) return null;
      const aggregateProductId = this.cleanValue(node.aggregateProduct?.value);
      if (aggregateProductId) return null;

      const product = {
        id: node.id,
        title: this.cleanValue(node.title),
        handle: this.cleanValue(node.handle),
        vendor: this.cleanValue(node.vendor),
        productType: this.cleanValue(node.productType),
        tags: Array.isArray(node.tags) ? node.tags : [],
        // 产品所属系列 handle 列表:全店拉取模式下顶部分类 tab 靠它做前端过滤
        collections: Array.isArray(node.collections?.nodes)
          ? node.collections.nodes.map((item) => this.cleanValue(item?.handle)).filter(Boolean)
          : [],
        onlineStoreUrl: this.cleanValue(node.onlineStoreUrl),
        image: this.resolveProductImage(node)
      };

      this.metafieldRefs.forEach((ref) => {
        const value = this.metafieldValue(node[ref.alias]);
        product[ref.canonical] = value;
        if (!product[ref.key]) product[ref.key] = value;
        const fileUrl = this.metafieldReferenceUrl(node[ref.alias]);
        if (fileUrl) product[`fileUrl:${ref.canonical}`] = fileUrl;
      });

      this.cardSpecs.forEach((spec) => {
        spec.fields.forEach((field) => {
          if (!field.displayField) return;
          const ref = this.metafieldRefs.find((item) => item.canonical === field.canonical);
          if (!ref) return;
          product[this.cardSpecFieldKey(field)] = this.metafieldValue(node[ref.alias], field.displayField);
        });
      });

      product.resourceCategory = product['custom.resource_category'] || product.resource_category || '';
      product.category = product['custom.category'] || product.category || '';

      return product;
    }

    getProductValue(product, key) {
      if (!product || !key) return '';
      const ref = this.normalizeMetafieldRef(key);
      if (product[ref.canonical] != null) return product[ref.canonical];
      if (product[key] != null) return product[key];
      if (product[ref.key] != null) return product[ref.key];
      return '';
    }

    getFirstProductValue(product, keys) {
      const keyList = Array.isArray(keys) ? keys : [keys];
      for (const key of keyList) {
        const value = typeof key === 'object'
          ? product[this.cardSpecFieldKey(key)]
          : this.getProductValue(product, key);
        if (this.cleanValue(value)) return value;
      }
      return '';
    }

    metafieldValue(field, displayField = '') {
      const values = [];
      const raw = this.formatMetafieldRawValue(field);
      if (raw) values.push(raw);

      const referenceValue = this.metaobjectText(field?.reference, displayField);
      if (referenceValue) values.push(referenceValue);

      const referenceNodes = field?.references?.nodes || [];
      referenceNodes.forEach((node) => {
        const value = this.metaobjectText(node, displayField);
        if (value) values.push(value);
      });

      return [...new Set(values)].join(' ');
    }

    formatMetafieldRawValue(field) {
      const raw = this.cleanValue(field?.value);
      if (!raw) return '';

      if (/^gid:\/\/shopify\//.test(raw)) return '';

      if (!/^[\[{]/.test(raw)) return raw;

      try {
        const parsed = JSON.parse(raw);
        if (field?.type === 'rich_text_field' || this.isRichTextNode(parsed)) {
          return this.richTextToText(parsed);
        }

        const flattened = this.stringifyMetafieldJson(parsed);
        if (/^gid:\/\/shopify\//.test(flattened)) return '';
        return flattened;
      } catch (_error) {
        return raw;
      }
    }

    parseFilterGroups(raw) {
      return String(raw || '')
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [label, key, optionsRaw] = line.split('|');
          const ref = this.normalizeMetafieldRef(key);
          const options = String(optionsRaw || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
            .map((item) => ({ label: item, value: item }));
          return {
            label: this.cleanValue(label),
            key: ref.canonical,
            options
          };
        })
        .filter((group) => group.label && group.key && group.options.length);
    }

    parseMetaobjectFilterGroups(raw) {
      return String(raw || '')
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [label, type, key, displayField, valueField] = line.split('|').map((item) => this.cleanValue(item));
          return {
            label,
            type,
            key: this.normalizeMetafieldRef(key).canonical,
            displayField,
            valueField
          };
        })
        .filter((group) => group.label && group.type && group.key);
    }

    parseCardSpecs(raw) {
      const specs = String(raw || '')
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [label, keysRaw, displayFieldRaw] = line.split('|');
          const displayField = this.cleanValue(displayFieldRaw);
          const fields = String(keysRaw || '')
            .split(',')
            .map((item) => {
              const ref = this.normalizeMetafieldRef(item);
              return {
                canonical: ref.canonical,
                displayField: displayField || this.defaultCardSpecDisplayField(ref)
              };
            })
            .filter((field) => field.canonical);
          const cleanLabel = this.cleanValue(label);
          return {
            label: cleanLabel ? `${cleanLabel}:` : '',
            keys: fields.map((field) => field.canonical),
            fields
          };
        })
        .filter((spec) => spec.label && spec.fields.length);

      return specs.length ? specs : [
        { label: 'SoC:', keys: ['custom.system_on_chip', 'custom.soc'], fields: [{ canonical: 'custom.system_on_chip', displayField: '' }, { canonical: 'custom.soc', displayField: '' }] },
        { label: 'Connector:', keys: ['custom.connector_type', 'custom.connector'], fields: [{ canonical: 'custom.connector_type', displayField: '' }, { canonical: 'custom.connector', displayField: '' }] },
        { label: 'Dimension:', keys: ['custom.dimension'], fields: [{ canonical: 'custom.dimension', displayField: '' }] },
        { label: 'Level:', keys: ['custom.environment', 'custom.operating_temperature'], fields: [{ canonical: 'custom.environment', displayField: '' }, { canonical: 'custom.operating_temperature', displayField: '' }] }
      ];
    }

    defaultCardSpecDisplayField(ref) {
      if (ref.canonical === 'custom1.connector') return 'name';
      return '';
    }

    parseTableColumns(raw) {
      return String(raw || '')
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [label, keysRaw] = line.split('|');
          // 分号分段=堆叠行：每段各占一格中的一行，段内逗号仍是"取第一个有值"的备选；
          // 单段写法与旧格式完全等价（旧配置无需迁移）
          const groups = String(keysRaw || '')
            .split(';')
            .map((seg) => seg
              .split(',')
              .map((item) => this.normalizeMetafieldRef(item).canonical)
              .filter(Boolean))
            .filter((fields) => fields.length);
          return {
            label: this.cleanValue(label),
            fields: [...new Set(groups.flat())],
            groups
          };
        })
        .filter((column) => column.label && column.fields.length);
    }

    collectMetafieldRefs() {
      const refs = [
        'custom.resource_category',
        'custom.subcategory',
        'custom.original_name',
        'custom.category',
        'custom.chip_platform',
        'custom.chip_type',
        'custom.system_on_chip',
        'custom.soc',
        'custom.npu',
        'custom.cpu',
        'custom.environment',
        'custom.operating_temperature',
        'custom.connector_type',
        'custom.connector',
        'custom.video_encoding',
        'custom.video_decoding',
        'custom.video_input',
        'custom.video_output',
        'custom.dimension'
      ];

      this.metaobjectFilterGroupConfigs.forEach((group) => refs.push(group.key));
      this.manualFilterGroups.forEach((group) => refs.push(group.key));
      this.selectedFilters.forEach((_values, key) => refs.push(key));
      if (this.cardRibbonField) refs.push(this.cardRibbonField);
      if (this.cardTitleField) refs.push(this.cardTitleField);
      if (this.groupField) refs.push(this.groupField);
      if (this.datasheetField) refs.push(this.datasheetField);
      this.tableColumns.forEach((column) => {
        column.fields.forEach((field) => refs.push(field));
      });
      this.cardSpecs.forEach((spec) => {
        spec.fields.forEach((field) => refs.push(field.canonical));
      });

      const seen = new Set();
      return refs
        .map((ref) => this.normalizeMetafieldRef(ref))
        .filter((ref) => ref.namespace && ref.key)
        .filter((ref) => {
          if (seen.has(ref.canonical)) return false;
          seen.add(ref.canonical);
          return true;
        })
        .map((ref, index) => ({
          ...ref,
          alias: `mf_${index}`
        }));
    }

    normalizeMetafieldRef(value) {
      const raw = this.cleanValue(value);
      if (!raw) return { namespace: 'custom', key: '', canonical: '', shortKey: '' };
      const dotIndex = raw.indexOf('.');
      const namespace = dotIndex > 0 ? raw.slice(0, dotIndex).trim() : 'custom';
      const key = dotIndex > 0 ? raw.slice(dotIndex + 1).trim() : raw;
      return {
        namespace,
        key,
        canonical: namespace && key ? `${namespace}.${key}` : '',
        shortKey: key
      };
    }

    cardSpecFieldKey(field) {
      const displayField = this.cleanValue(field?.displayField);
      return displayField ? `${field.canonical}::${displayField}` : field.canonical;
    }

    metaobjectOptionFromNode(node, group) {
      if (!node) return null;
      const fieldMap = this.metaobjectFieldMap(node);
      const label = this.metaobjectDisplayText(node, group);
      if (!label) return null;
      const value = this.metaobjectMatchText(node, group, label);
      const identityKeys = this.metaobjectIdentityKeys(node, label, value);
      const belongingPlatformKeys = this.normalizeReferenceKeys(fieldMap.get('belonging_platform'));

      const sortRaw = fieldMap.get('sort_order') || fieldMap.get('sort') || fieldMap.get('order') || fieldMap.get('position');
      const sortValue = Number(sortRaw);

      return {
        key: this.cleanValue(node.id) || this.normalize(label),
        label,
        value,
        identityKeys,
        belongingPlatformKeys,
        sort: Number.isFinite(sortValue) ? sortValue : 999999
      };
    }

    metaobjectIdentityKeys(node, label = '', value = '') {
      return [...new Set([
        node?.id,
        node?.handle,
        label,
        value
      ].map((item) => this.normalizeReferenceKey(item)).filter(Boolean))];
    }

    normalizeReferenceKeys(rawValue) {
      const raw = this.cleanValue(rawValue);
      if (!raw) return [];

      let values = [raw];
      if (/^[\[\"]/.test(raw)) {
        try {
          const parsed = JSON.parse(raw);
          values = Array.isArray(parsed) ? parsed : [parsed];
        } catch (_error) {
          values = [raw];
        }
      }

      return [...new Set(values.map((item) => this.normalizeReferenceKey(item)).filter(Boolean))];
    }

    normalizeReferenceKey(value) {
      return this.cleanValue(value).toLowerCase();
    }

    metaobjectText(node, displayField = '') {
      if (!node) return '';
      const fieldMap = this.metaobjectFieldMap(node);
      if (displayField) return this.cleanValue(fieldMap.get(displayField));
      return this.metaobjectDisplayText(node) || this.metaobjectFieldValuesText(node);
    }

    metaobjectFieldValuesText(node) {
      const fields = Array.isArray(node?.fields) ? node.fields : [];
      const values = fields
        .filter((field) => !/^(sort|sort_order|order|position)$/i.test(this.cleanValue(field?.key)))
        .map((field) => this.cleanValue(field?.value))
        .filter((value) => value && !/^gid:\/\/shopify\//.test(value));

      return [...new Set(values)].join(' ');
    }

    metaobjectDisplayText(node, group = {}) {
      const fieldMap = this.metaobjectFieldMap(node);
      return this.cleanValue(
        (group.displayField && fieldMap.get(group.displayField)) ||
        this.firstMetaobjectField(fieldMap, ['connector_name', 'connector_value', 'parameter_name', 'parameter_value', 'param_name', 'param_value', 'display_name', 'name', 'title', 'label', 'value'])
      );
    }

    metaobjectMatchText(node, group = {}, fallback = '') {
      const fieldMap = this.metaobjectFieldMap(node);
      if (group.valueField) return this.cleanValue(fieldMap.get(group.valueField));
      const values = [
        fallback,
        this.firstMetaobjectField(fieldMap, ['connector_name', 'connector_value', 'parameter_name', 'parameter_value', 'param_name', 'param_value', 'display_name', 'name', 'title', 'label', 'value']),
        node.handle
      ];
      return [...new Set(values.map((item) => this.cleanValue(item)).filter(Boolean))].join(' ');
    }

    firstMetaobjectField(fieldMap, keys) {
      for (const key of keys) {
        const value = this.cleanValue(fieldMap.get(key));
        if (value) return value;
      }
      return '';
    }

    metaobjectFieldMap(node) {
      const fields = Array.isArray(node?.fields) ? node.fields : [];
      return new Map(fields.map((field) => [this.cleanValue(field?.key), this.cleanValue(field?.value)]));
    }

    isRichTextNode(value) {
      return Boolean(value && typeof value === 'object' && typeof value.type === 'string' && Array.isArray(value.children));
    }

    richTextToText(node) {
      if (!node) return '';

      if (Array.isArray(node)) {
        return node.map((item) => this.richTextToText(item)).filter(Boolean).join(', ');
      }

      if (typeof node !== 'object') {
        return this.cleanValue(node);
      }

      if (node.type === 'text') {
        return this.cleanValue(node.value);
      }

      const children = Array.isArray(node.children) ? node.children : [];

      if (node.type === 'list') {
        return children.map((item) => this.richTextToText(item)).filter(Boolean).join(', ');
      }

      if (node.type === 'list-item' || node.type === 'paragraph') {
        return children.map((item) => this.richTextToText(item)).filter(Boolean).join('');
      }

      return children.map((item) => this.richTextToText(item)).filter(Boolean).join(', ');
    }

    stringifyMetafieldJson(value) {
      if (value === null || value === undefined) return '';

      if (Array.isArray(value)) {
        return value
          .map((item) => this.stringifyMetafieldJson(item))
          .filter(Boolean)
          .join(', ');
      }

      if (typeof value === 'object') {
        if (this.isRichTextNode(value)) return this.richTextToText(value);
        if (typeof value.value === 'string' || typeof value.value === 'number') return this.cleanValue(value.value);
        if (typeof value.name === 'string') return this.cleanValue(value.name);
        if (typeof value.title === 'string') return this.cleanValue(value.title);
        if (typeof value.label === 'string') return this.cleanValue(value.label);

        return Object.values(value)
          .map((item) => this.stringifyMetafieldJson(item))
          .filter((item) => item && !/^gid:\/\/shopify\//.test(item))
          .join(', ');
      }

      return this.cleanValue(value);
    }

    setStatus(text) {
      if (this.nodes.status) {
        this.nodes.status.textContent = text || '';
        this.nodes.status.hidden = !text;
      }
    }

    async storefrontRequest(query, variables) {
      const endpoint = `https://${this.storeDomain.replace(/^https?:\/\//, '').replace(/\/$/, '')}/api/2025-10/graphql.json`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({ query, variables })
      });
      const payload = await response.json();
      if (!response.ok || payload.errors?.length) {
        throw new Error(payload.errors?.[0]?.message || response.statusText);
      }
      return payload;
    }

    productFields() {
      return `
        id
        title
        handle
        vendor
        productType
        tags
        onlineStoreUrl
        aggregateProduct: metafield(namespace: "custom", key: "aggregate_product") {
          value
        }
        featuredImage {
          url(transform: { maxWidth: 640 })
          altText
          width
          height
        }
        thumbnail: metafield(namespace: "custom", key: "thumbnail") {
          value
          reference {
            ... on MediaImage {
              image {
                url(transform: { maxWidth: 640 })
                altText
              }
            }
            ... on GenericFile {
              url
            }
          }
        }
        collections(first: 30) {
          nodes {
            handle
          }
        }
        ${this.metafieldRefs.map((ref) => this.metafieldQuery(ref)).join('\n')}
      `;
    }

    metafieldQuery(ref) {
      return `
        ${ref.alias}: metafield(namespace: "${this.escapeGraphqlString(ref.namespace)}", key: "${this.escapeGraphqlString(ref.key)}") {
          type
          value
          reference {
            ... on Metaobject {
              id
              handle
              fields {
                key
                value
              }
            }
            ... on GenericFile {
              url
            }
            ... on MediaImage {
              image {
                url
              }
            }
          }
          references(first: 10) {
            nodes {
              ... on Metaobject {
                id
                handle
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      `;
    }

    escapeGraphqlString(value) {
      return this.cleanValue(value)
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"');
    }

    metaobjectsQuery() {
      return `
        query ProductCenterMetaobjects($type: String!, $first: Int!, $after: String) {
          metaobjects(type: $type, first: $first, after: $after) {
            edges {
              node {
                id
                handle
                fields {
                  key
                  value
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;
    }

    productsQuery() {
      // sortKey CREATED_AT + reverse：最新创建的产品排在前面（默认 ID 升序会把新品排最后）
      return `
        query ProductCenterAllProducts($first: Int!, $after: String) {
          products(first: $first, after: $after, sortKey: CREATED_AT, reverse: true) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                ${this.productFields()}
              }
            }
          }
        }
      `;
    }

    productSearchQuery() {
      // 用在线商店搜索引擎(search 根字段,与 /search 页面同引擎):支持部分词匹配,
      // 搜 3566 能命中 title 含 RK3566/R3566 的产品——原先的 products(query:) 是
      // 整词过滤,子串搜不到。search 无 type 参数,返回 Product/Page/Article
      // 混合 union,这里只声明 Product 片段,消费端按 __typename 过滤
      return `
        query ProductCenterSearchProducts($first: Int!, $after: String, $query: String!) {
          search(query: $query, first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                __typename
                ... on Product {
                  ${this.productFields()}
                }
              }
            }
          }
        }
      `;
    }

    collectionProductsQuery() {
      return `
        query ProductCenterProducts($handle: String!, $first: Int!, $after: String) {
          collection(handle: $handle) {
            products(first: $first, after: $after) {
              pageInfo {
                hasNextPage
                endCursor
              }
              edges {
                node {
                  ${this.productFields()}
                }
              }
            }
          }
        }
      `;
    }

    cleanValue(value) {
      return String(value || '').trim();
    }

    normalize(value) {
      return this.cleanValue(value).toLowerCase().replace(/\s+/g, ' ');
    }

    normalizeUrlToken(value) {
      return this.normalize(value).replace(/[-_]+/g, ' ');
    }

    escapeHtml(value) {
      return this.cleanValue(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  customElements.define('product-center', ProductCenter);
}
